import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useSEO } from "@/lib/seo";
import { api, errorMessage, mediaUrl } from "@/lib/api";
import { Field, Input, Textarea, useForm } from "@/components/site/Fields";
import { Reveal } from "@/components/site/Motion";

export default function Sell() {
  const { t, lang } = useLang();
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const [v, , bind] = useForm({ make: "", model: "", version: "", year: "", mileage: "", location: "", exterior_color: "", interior_color: "", ownership_history: "", service_history: "", asking_price: "", description: "", name: "", email: "", phone: "", whatsapp: "" });
  useSEO({
    title: lang === "fr" ? "Vendre une voiture de prestige ou de collection — Palissiere Motor Group" : "Sell a prestige or collector car — Palissiere Motor Group",
    description: lang === "fr" ? "Confiez la vente de votre véhicule d'exception à un courtier automobile international. Évaluation confidentielle et mise en relation avec des acheteurs qualifiés." : "Entrust the sale of your exceptional vehicle to an international automotive broker. Confidential assessment and introduction to qualified buyers.",
    lang,
  });

  const upload = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/")).slice(0, 12);
    if (!list.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/media/public-upload", fd);
      setPhotos((p) => [...p, ...data]);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setUploading(false);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { name, email, phone, whatsapp, description, ...data } = v;
      await api.post("/leads/sell", { name, email, phone, whatsapp, message: description, vehicle: [v.make, v.model, v.version, v.year].filter(Boolean).join(" "), data, photos: photos.map((p) => p.url), lang });
      setDone(true);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#070809] pt-28 md:pt-36">
      <div className="container-x grid gap-14 pb-24 lg:grid-cols-12 md:pb-32">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow mb-5">{t("sell_eyebrow")}</p>
          <h1 className="display text-4xl sm:text-5xl" data-testid="sell-title">{t("se_title")}</h1>
          <p className="mt-6 text-base leading-relaxed text-[#c7ccd4]">{t("se_copy")}</p>
          <p className="mono mt-10 max-w-xs text-[0.6rem] leading-relaxed text-[#8b95a5]">{t("se_confidential")}</p>
        </Reveal>
        <div className="lg:col-span-7 lg:col-start-6">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 p-10" data-testid="sell-success">
              <Check className="text-[#e2c89d]" />
              <h2 className="display-tight mt-6 text-2xl">{t("se_done_title")}</h2>
              <p className="mt-3 text-[#c7ccd4]">{t("se_done_copy")}</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="sell-form" className="grid gap-7 sm:grid-cols-2">
              <Field label={t("s_make")} required><Input data-testid="sell-make" required {...bind("make")} /></Field>
              <Field label={t("s_model")} required><Input data-testid="sell-model" required {...bind("model")} /></Field>
              <Field label={t("s_version")}><Input data-testid="sell-version" {...bind("version")} /></Field>
              <Field label={t("s_year")}><Input data-testid="sell-year" type="number" {...bind("year")} /></Field>
              <Field label={t("s_mileage")}><Input data-testid="sell-mileage" type="number" {...bind("mileage")} /></Field>
              <Field label={t("s_location")}><Input data-testid="sell-location" {...bind("location")} /></Field>
              <Field label={t("s_ext")}><Input data-testid="sell-ext-color" {...bind("exterior_color")} /></Field>
              <Field label={t("s_int")}><Input data-testid="sell-int-color" {...bind("interior_color")} /></Field>
              <Field label={t("se_ownership")}><Input data-testid="sell-ownership" {...bind("ownership_history")} /></Field>
              <Field label={t("se_service")}><Input data-testid="sell-service" {...bind("service_history")} /></Field>
              <Field label={`${t("se_asking")} (€)`} className="sm:col-span-2"><Input data-testid="sell-asking-price" type="number" step="1000" {...bind("asking_price")} /></Field>
              <Field label={t("se_desc")} className="sm:col-span-2"><Textarea data-testid="sell-description" rows={4} {...bind("description")} /></Field>

              <div className="sm:col-span-2">
                <p className="mono mb-3 text-[0.6rem] text-[#8b95a5]">{t("se_photos")}</p>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
                  onClick={() => inputRef.current?.click()}
                  data-testid="sell-photo-dropzone"
                  className="flex cursor-pointer flex-col items-center justify-center gap-3 border border-dashed border-white/25 p-8 text-center transition-colors hover:border-white/50"
                >
                  <ImagePlus className="text-[#8b95a5]" />
                  <p className="text-sm text-[#c7ccd4]">{uploading ? "…" : t("se_photos_hint")}</p>
                  <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="sell-photo-input" />
                </div>
                {photos.length > 0 && (
                  <div className="mt-4 grid grid-cols-4 gap-2 sm:grid-cols-6" data-testid="sell-photo-previews">
                    {photos.map((p, i) => (
                      <div key={p.id || i} className="relative aspect-square overflow-hidden bg-[#0e1013]">
                        <img src={mediaUrl(p.thumb || p.url)} alt="" className="h-full w-full object-cover" />
                        <button type="button" onClick={() => setPhotos((s) => s.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/70 p-1"><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="sm:col-span-2 mt-4 border-t border-white/10 pt-8"><p className="eyebrow mb-6">{t("so_step4")}</p></div>
              <Field label={t("f_name")} required><Input data-testid="sell-name" required {...bind("name")} /></Field>
              <Field label={t("f_email")} required><Input data-testid="sell-email" type="email" required {...bind("email")} /></Field>
              <Field label={t("f_phone")}><Input data-testid="sell-phone" type="tel" {...bind("phone")} /></Field>
              <Field label={t("f_whatsapp")}><Input data-testid="sell-whatsapp" type="tel" {...bind("whatsapp")} /></Field>
              <div className="sm:col-span-2 mt-4 flex flex-wrap items-center justify-between gap-4">
                <span className="mono text-[0.58rem] text-[#8b95a5]">* {t("f_required")}</span>
                <button type="submit" disabled={busy || uploading} data-testid="sell-form-submit" className="btn btn-primary disabled:opacity-60">{t("se_submit")}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
